import { useEffect, useState } from 'react';
import { AsyncPaginate, LoadOptions } from 'react-select-async-paginate';
import { GroupBase } from 'react-select';
import { extractListItemsFromHTML } from '../../../utility/helpers';
import { useAppDispatch } from '../../../store/hooks';
import { getCertificates } from '../../../store/features/dashboard';
import { Button, Form, Image } from 'react-bootstrap';
import { greenArrowRight } from '../../../assets/images/images';

type OptionType = {
  label: string;
  value: string;
};

type Additional = {
  page: number;
};

const CERTIFICATE_PLACEHOLDER_TEXT = "Click the expand icon to add the certificates.";

const parseCertificatesFromValue = (rawValue: any): OptionType[] => {
  if (typeof rawValue !== "string" || !rawValue.trim()) {
    return [];
  }

  // Certificate entries are expected as list items; ignore plain placeholder text.
  const hasListItems = /<li[\s>]/i.test(rawValue);
  if (!hasListItems) {
    return [];
  }

  const parsedValue = extractListItemsFromHTML(rawValue);
  if (!parsedValue?.length) {
    return [];
  }

  return parsedValue
    .map((item: string) => item.trim())
    .filter((item: string) => item && item !== CERTIFICATE_PLACEHOLDER_TEXT)
    .map((item: string) => ({
      label: item,
      value: item,
    }));
};

const CertificateEdit = ({ value, setModalData }: any) => {
  const dispatch = useAppDispatch();
  const [selectedCerts, setSelectedCerts] = useState<OptionType[]>(() =>
    parseCertificatesFromValue(value)
  );
  const [inputValue, setInputValue] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [newItem, setNewItem] = useState("");
  
  const fetchCertificates = async (search: string, page: number) => {
    const res: any = await dispatch(getCertificates({ search, page, limit: 10 })).unwrap();
    return res?.data?.results || [];
  };

  const loadOptions: LoadOptions<OptionType, GroupBase<OptionType>, Additional> = async (
    inputValue,
    loadedOptions,
    additional
  ) => {
    const page = additional?.page || 1;

    if (!inputValue || inputValue.trim().length <= 2) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      };
    }

    try {
      const certsList: string[] = await fetchCertificates(inputValue, page);

      const options = certsList.map((cert: string) => ({
        label: cert,
        value: cert,
      }));

      return {
        options,
        hasMore: certsList.length === 10,
        additional: {
          page: page + 1,
        },
      };
    } catch {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: page + 1,
        },
      };
    }
  };

  const handleSelect = (selected: OptionType | null) => {
    if (selected) {
      setSelectedCerts(prev => [...prev, selected]);
      setInputValue('');
    }
  };

  useEffect(() => {
    if (setModalData && typeof setModalData === 'function') {
      if (selectedCerts.length === 0) {
        setModalData('');
      } else {
        const htmlList = `<ul>${selectedCerts
          .map((item) => `<li>${item.label}</li>`)
          .join('')}</ul>`;
        setModalData(htmlList);
      }
    }
  }, [selectedCerts, setModalData]);

  const handleInput = () => {
    if (!newItem.trim()) {
      hideInput();
    }
  };

  const handleBlur = () => {
    if (newItem.trim()) {
      saveInput(newItem.trim());
    } else {
      hideInput();
    }
  };

  const hideInput = () => {
    setShowInput(false);
    setNewItem("");
  };

  const saveInput = (inputData: string) => {
    setSelectedCerts(prev => [...prev, { label: inputData, value: inputData }]);
    hideInput();
  }
  
  return (
    <div>
      <AsyncPaginate<OptionType, GroupBase<OptionType>, Additional>
        value={null}
        loadOptions={loadOptions}
        onChange={handleSelect}
        additional={{ page: 1 }}
        inputValue={inputValue}
        onInputChange={(value, { action }) => {
            if (action === 'input-change') {
              setInputValue(value);
            }
          }}
        isClearable
        debounceTimeout={300}
        placeholder="Type here to search certifications..."
        classNamePrefix="async-select"
      />

      <p className='drop-inner certificate-input'>
        {showInput && (
          <div className="input-container">
            <Form.Control
              type="text"
              placeholder="Type here"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onBlur={() => handleInput()}
              autoFocus
            />
            <Image
              src={greenArrowRight}
              height={30}
              width={30}
              alt="arrow"
              className="submit-button"
              onClick={() => handleBlur()}
            />
          </div>
        )}
        {!showInput && (<Button
          variant="button"
          className="text-green link-btn"
          onClick={() => setShowInput(!showInput)}
          size='sm'
        >
          + Add
        </Button>)}
      
      </p>

      {selectedCerts.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h6>Selected:</h6>
          <ul style={{ padding: 0, listStyle: 'none' }}>
            {selectedCerts.map((item, i) => (
              <li
                key={i}
              >
                {i+1}.{' '}<span>{item.label}</span>
                <span
                  onClick={() =>
                    setSelectedCerts(prev =>
                      prev.filter((_, index) => index !== i)
                    )
                  }
                  style={{
                    color: '#ff6060',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    marginLeft: '12px',
                    fontSize: '14px',
                  }}
                >
                  Remove
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CertificateEdit;
